/**
 * Tolerant readers for the trivia round payloads (GH-912, GH-914, and the server work in flight
 * adding end-of-question reveal facts, an autoplay leaderboard phase and a display show/hide
 * flag).
 *
 * ## Why these are hand-rolled narrowers and not a contracts schema
 *
 * `packages/contracts`' trivia shapes are being extended in parallel with this card, and the
 * donor card must keep rendering through that shift: a round status this build has never heard
 * of, a new field on the round, or a renamed reveal fact must degrade the card gracefully —
 * never blank it, and never make a poll tick throw. A strict schema fails the whole payload the
 * moment one enum gains a member; these readers take `unknown`, keep exactly what the card
 * draws, and answer `null` for anything they cannot read — the same "absence is not an error"
 * rule the card already applies to a missing round. Unknown fields ride along untouched (see
 * `readTriviaRoundState`) so the reveal seam below can be wired without re-plumbing the parse.
 */

import type { TriviaCorrectAnswerWire } from "@givehere/contracts";

export type TriviaDonorQuestion = {
  readonly id: string;
  readonly questionType: string;
  readonly prompt: string;
  readonly choices: readonly string[];
  /** 0 when the server did not say — the card then simply omits the points line. */
  readonly points: number;
  /** Null when the server did not say — the card then draws no countdown. */
  readonly timeLimitSeconds: number | null;
};

export type TriviaDonorRoundState = {
  readonly id: string;
  /**
   * Deliberately a plain string, not the contracts enum: the autoplay work in flight adds phases
   * ("lobby" | "active" | "between_questions" | "finished" are the ones this build knows), and an
   * unknown one renders as the quiet between-questions state rather than breaking the card.
   */
  readonly status: string;
  readonly currentQuestion: TriviaDonorQuestion | null;
  readonly questionStartedAt: string | null;
  /** GH-925: server-clock ISO deadline of the current phase; null when it holds indefinitely. */
  readonly phaseEndsAt?: string | null;
  /** The end-of-question reveal block, if the server sent one — read only by `readRevealFacts`. */
  readonly reveal?: unknown;
};

/** GH-925: the donor's own selection for the current question — no correctness. */
export type TriviaMyAnswer = {
  readonly questionId: string;
  readonly submittedAnswer: TriviaCorrectAnswerWire;
};

/** GH-925: the donor's own scored result — the server sends it only once the question closed. */
export type TriviaMyResult = {
  readonly questionId: string;
  readonly isCorrect: boolean;
  readonly pointsAwarded: number;
};

export type TriviaDonorRound = {
  readonly round: TriviaDonorRoundState | null;
  readonly myPlayerId: string | null;
  readonly myScore: number | null;
  readonly hasAnswered: boolean | null;
  readonly myAnswer?: TriviaMyAnswer | null;
  readonly myResult?: TriviaMyResult | null;
};

/* --------------------------------------------------------------------- narrowing */

const asRecord = (value: unknown): Readonly<Record<string, unknown>> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;

const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);

const asFiniteNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const asStringArray = (value: unknown): readonly string[] | null =>
  Array.isArray(value) && value.every((entry): entry is string => typeof entry === "string")
    ? value
    : null;

/* ----------------------------------------------------------------------- readers */

/** A question the card can actually draw, or null — never a half-readable one. */
export function readTriviaQuestion(value: unknown): TriviaDonorQuestion | null {
  const body = asRecord(value);
  if (body === null) return null;
  const id = asString(body["id"]);
  const questionType = asString(body["questionType"]);
  const prompt = asString(body["prompt"]);
  const choices = asStringArray(body["choices"]);
  if (id === null || questionType === null || prompt === null || choices === null) return null;
  if (choices.length === 0) return null;
  const points = asFiniteNumber(body["points"]);
  const timeLimitSeconds = asFiniteNumber(body["timeLimitSeconds"]);
  return {
    id,
    questionType,
    prompt,
    choices,
    points: points !== null && points > 0 ? points : 0,
    timeLimitSeconds: timeLimitSeconds !== null && timeLimitSeconds > 0 ? timeLimitSeconds : null,
  };
}

/**
 * The round state, or null when the payload cannot carry one this card could act on (no `id`
 * means no answer route to call; no `status` means no state to render). Fields this build does
 * not know are spread through at runtime — invisible to the type, available to the reveal seam —
 * so the in-flight server work can land without a donor-web re-release breaking mid-poll.
 */
export function readTriviaRoundState(value: unknown): TriviaDonorRoundState | null {
  const body = asRecord(value);
  if (body === null) return null;
  const id = asString(body["id"]);
  const status = asString(body["status"]);
  if (id === null || status === null) return null;
  return {
    ...body,
    id,
    status,
    currentQuestion: readTriviaQuestion(body["currentQuestion"]),
    questionStartedAt: asString(body["questionStartedAt"]),
    phaseEndsAt: asString(body["phaseEndsAt"]),
  };
}

/** `{choiceIndex}` or `{order}` as the contracts type, or null for anything else. */
export function readCorrectAnswer(value: unknown): TriviaCorrectAnswerWire | null {
  const body = asRecord(value);
  if (body === null) return null;
  const choiceIndex = asFiniteNumber(body["choiceIndex"]);
  if (choiceIndex !== null) return { choiceIndex };
  const order = body["order"];
  if (Array.isArray(order) && order.every((entry): entry is number => typeof entry === "number")) {
    return { order };
  }
  return null;
}

function readMyAnswer(value: unknown): TriviaMyAnswer | null {
  const body = asRecord(value);
  if (body === null) return null;
  const questionId = asString(body["questionId"]);
  const submittedAnswer = readCorrectAnswer(body["submittedAnswer"]);
  return questionId === null || submittedAnswer === null ? null : { questionId, submittedAnswer };
}

function readMyResult(value: unknown): TriviaMyResult | null {
  const body = asRecord(value);
  if (body === null) return null;
  const questionId = asString(body["questionId"]);
  const pointsAwarded = asFiniteNumber(body["pointsAwarded"]);
  if (questionId === null || pointsAwarded === null || typeof body["isCorrect"] !== "boolean") {
    return null;
  }
  return { questionId, isCorrect: body["isCorrect"], pointsAwarded };
}

/** `GET /v1/donor/events/:eventId/trivia/round`, read tolerantly. Garbage is "no round". */
export function parseTriviaDonorRound(value: unknown): TriviaDonorRound {
  const body = asRecord(value);
  return {
    round: body === null ? null : readTriviaRoundState(body["round"]),
    myPlayerId: body === null ? null : asString(body["myPlayerId"]),
    myScore: body === null ? null : asFiniteNumber(body["myScore"]),
    hasAnswered:
      body !== null && typeof body["hasAnswered"] === "boolean" ? body["hasAnswered"] : null,
    myAnswer: body === null ? null : readMyAnswer(body["myAnswer"]),
    myResult: body === null ? null : readMyResult(body["myResult"]),
  };
}

/**
 * "Is there a round somebody could join", from the PUBLIC big-screen payload
 * (`GET /v1/public/events/:eventId/trivia/round`). One boolean, nothing else: that payload also
 * carries the question, tally, leaderboard and background, all of which are the big screen's
 * business — the pre-check-in gate must not surface more than "there is something to join".
 * A finished round is still served there (it holds the final standings for the wall) but is not
 * joinable; an unreadable payload is simply "nothing to join".
 */
export function isPublicTriviaRoundJoinable(value: unknown): boolean {
  const body = asRecord(value);
  if (body === null) return false;
  const round = asRecord(body["round"]);
  if (round === null) return false;
  return asString(round["status"]) !== "finished";
}

/* --------------------------------------------------------------------- the seam */

/** End-of-question reveal facts, when the server sent them. See `readRevealFacts`. */
export type TriviaRevealFacts = {
  /** The correct answer, already composed as the words the card would print, or null. */
  readonly correctAnswerLabel: string | null;
  /** The first correct answerer's shoutout — big-screen material; the donor card ignores it. */
  readonly firstCorrect: {
    readonly displayName: string;
    readonly secondsAfterStart: number;
  } | null;
};

/**
 * SEAM between this card and the round-flow server work (GH-916), kept to this one function on
 * purpose. Written against the shape that work had landed in the working tree when this was
 * built — `round.reveal: { correctAnswer, firstCorrect: {displayName, secondsAfterStart} | null,
 * correctResponses }`, carried only once the question can no longer be answered — and read
 * tolerantly like everything else here: if those fields move again, this answers null, the
 * between-questions state simply loses its "The right answer" line, and reconciling means
 * editing this body and its pinned test, nothing else.
 *
 * The donor card deliberately uses only the correct-answer label. `firstCorrect` and
 * `correctResponses` are the big screen's spectacle; this phone stays personal (your answer,
 * your result, your score).
 */
export function readRevealFacts(round: TriviaDonorRoundState): TriviaRevealFacts | null {
  const reveal = asRecord(round.reveal);
  if (reveal === null) return null;
  const correct = asRecord(reveal["correctAnswer"]);
  const choices = round.currentQuestion?.choices ?? null;
  const first = asRecord(reveal["firstCorrect"]);
  const firstName = first === null ? null : asString(first["displayName"]);
  const firstSeconds = first === null ? null : asFiniteNumber(first["secondsAfterStart"]);
  return {
    correctAnswerLabel: correct === null ? null : labelCorrectAnswer(correct, choices),
    firstCorrect:
      firstName !== null && firstSeconds !== null
        ? { displayName: firstName, secondsAfterStart: firstSeconds }
        : null,
  };
}

/** `{choiceIndex}` or `{order}`, in the words of the question's own choices where they exist. */
function labelCorrectAnswer(
  correct: Readonly<Record<string, unknown>>,
  choices: readonly string[] | null,
): string | null {
  const choiceIndex = asFiniteNumber(correct["choiceIndex"]);
  if (choiceIndex !== null) return choices?.[choiceIndex] ?? `#${choiceIndex + 1}`;
  const order = correct["order"];
  if (Array.isArray(order) && order.every((entry): entry is number => typeof entry === "number")) {
    return order.map((index) => choices?.[index] ?? `#${index + 1}`).join(" → ");
  }
  return null;
}
